import type { SalesListQuery } from '../../../api/endpoints';
import type { BulkResult, BulkTask, EtaResultRow, SalesDocumentSummary } from '../../../api/types';
import { LIVE_STATES } from '../status';

/**
 * What the batch page shows for each document (WP-1b.3, BLOCK-1B-BULK-DESIGN §2, §5): the outcome
 * of a result, whether it may be tried again, its reason in words, and the order of the tables.
 * The rules themselves (which documents a task applies to) are the server's `bulkEligibility`.
 */

/**
 * `unconfirmed` (RIB-02, DEC-046): the call's answer was lost and the document moved since, but not
 * to the task's end state; its own term in the summary («لم تُؤكَّد»), never «لم يكتمل».
 */
export type Outcome = 'done' | 'waiting' | 'skipped' | 'failed' | 'unconfirmed';

/** Never tried again: the task does not apply to the document (§5 «تُخطّي»). */
export const SKIP_CODES: ReadonlySet<string> = new Set([
  'not_found',
  'document_state_conflict',
  'validation_error',
  'permission_denied',
  'not_implemented',
]);

/** Not completed on our side: a retry may complete it (§5 «لم يكتمل»). */
export const RETRY_CODES: ReadonlySet<string> = new Set([
  'version_conflict',
  'internal_error',
  'eta_unavailable',
  'network_error',
  'unexpected_response',
]);

/** ETA states that end a send without a valid document (the Tax Authority's answer, D-2 v2). */
const ETA_REFUSED: ReadonlySet<string> = new Set([
  'invalid',
  'not_accepted',
  'rejected',
  'cancelled',
]);

export interface RowState {
  outcome: Outcome;
  /** May «أعِد محاولة ما لم يكتمل» run it again? */
  retryable: boolean;
  /** Failed because the Tax Authority answered (shown with its answer; never retried). */
  etaAnswer: boolean;
}

/** The outcome of one result row; `fileFailed`: its PDF could not be fetched (PDF task). */
export function rowState(task: BulkTask, row: BulkResult, fileFailed = false): RowState {
  if (!row.ok) {
    const code = row.code ?? 'internal_error';
    if (SKIP_CODES.has(code)) return { outcome: 'skipped', retryable: false, etaAnswer: false };
    if (row.details?.unconfirmed)
      return { outcome: 'unconfirmed', retryable: RETRY_CODES.has(code), etaAnswer: false };
    return { outcome: 'failed', retryable: RETRY_CODES.has(code), etaAnswer: false };
  }
  if (task === 'pdf' && fileFailed) return { outcome: 'failed', retryable: true, etaAnswer: false };
  if (task === 'send') {
    const eta = row.etaStatus ?? null;
    if (eta === 'valid') return { outcome: 'done', retryable: false, etaAnswer: false };
    if (eta && ETA_REFUSED.has(eta))
      return { outcome: 'failed', retryable: false, etaAnswer: true };
    // Sent, the Tax Authority's answer still on its way (followed by polling).
    return { outcome: 'waiting', retryable: false, etaAnswer: false };
  }
  return { outcome: 'done', retryable: false, etaAnswer: false };
}

/** Is the row still followed (its ETA status will change on its own)? */
export const isLive = (row: BulkResult) =>
  row.ok && (!row.etaStatus || LIVE_STATES.has(row.etaStatus));

const ORDER: Record<Outcome, number> = {
  failed: 0,
  unconfirmed: 1,
  waiting: 2,
  skipped: 3,
  done: 4,
};

/**
 * Results order, fixed when a run ends: not completed, not confirmed, with ETA now, skipped,
 * completed (§5).
 */
export function resultOrder(
  task: BulkTask,
  results: readonly BulkResult[],
  fileFailed: ReadonlySet<string> = new Set(),
): string[] {
  return results
    .map((r, i) => ({ id: r.id, i, o: ORDER[rowState(task, r, fileFailed.has(r.id)).outcome] }))
    .sort((a, b) => a.o - b.o || a.i - b.i)
    .map((x) => x.id);
}

/** Review order: the rows to be skipped first, then the batch order (§4). */
export function reviewOrder(results: readonly BulkResult[]): BulkResult[] {
  return results
    .map((r, i) => ({ r, i }))
    .sort((a, b) => Number(a.r.ok) - Number(b.r.ok) || a.i - b.i)
    .map((x) => x.r);
}

/** Our reason texts (`batch.reasons.*`) and the single actions' code texts (`errors.codes.*`). */
export type ReasonKey =
  | 'notFound'
  | 'sentBefore'
  | 'notDraft'
  | 'notSendable'
  | 'incomplete'
  | 'permission'
  | 'changed'
  | 'ourSide'
  | 'notReached';
export type Reason = { reason: ReasonKey } | { errorCode: string };

/** The reason of a refused or failed row (§2, §5); an ETA-environment refusal keeps the single
 * action's own text (BD-10). */
export function reasonOf(task: BulkTask, row: BulkResult): Reason {
  const code = row.code ?? 'internal_error';
  const details = row.details ?? {};
  switch (code) {
    case 'not_found':
      return { reason: 'notFound' };
    case 'document_state_conflict':
      if (task === 'discard') return { reason: 'notDraft' };
      return { reason: details.etaStatus ? 'sentBefore' : 'notSendable' };
    case 'validation_error':
      return { reason: 'incomplete' };
    case 'permission_denied':
      return details.etaEnvironment ? { errorCode: code } : { reason: 'permission' };
    case 'version_conflict':
      return { reason: 'changed' };
    case 'network_error':
      return { reason: 'notReached' };
    case 'internal_error':
      return { reason: 'ourSide' };
    default:
      return { errorCode: code };
  }
}

/**
 * The Tax Authority's answer on a refused document, from its verdict rows (WP-1b.2, DEC-022): the
 * first row that carries a code, in the Authority's order, else the first failing step; its text in
 * the interface language first. Null when the verdict holds nothing to show.
 */
export function etaAnswerOf(
  rows: readonly EtaResultRow[],
  locale: 'ar' | 'en',
): { code: string; text: string | null } | null {
  const coded = rows.find((r) => r.errorCode);
  const failing =
    coded ?? rows.find((r) => r.stepStatus && !/^valid$/i.test(r.stepStatus)) ?? rows[0];
  if (!failing) return null;
  const code = failing.errorCode ?? failing.stepName ?? failing.etaStatus;
  const text =
    locale === 'ar'
      ? (failing.messageAr ?? failing.messageEn)
      : (failing.messageEn ?? failing.messageAr);
  return { code, text: text ?? null };
}

/** The list query of the step-2 preview and of put-all: the carried criteria AND the task. */
export function candidateQuery(
  task: BulkTask,
  criteria: SalesListQuery,
  page: number,
  limit: number,
): SalesListQuery {
  return { ...criteria, tab: 'invoices', eligibleFor: task, page, limit };
}

/** A document as a result row's fields (the reading a re-read shows). */
function snapshot(doc: SalesDocumentSummary): Partial<BulkResult> {
  return {
    number: doc.number,
    status: doc.status,
    etaStatus: doc.etaStatus,
    readiness: doc.readiness,
    version: doc.version,
    totalAmount: doc.totalAmount,
    receiverName: doc.receiverName,
    cash: doc.cash,
  };
}

/** The document as the batch knew it before the call (its batch item). */
export interface BeforeCall {
  status: string;
  etaStatus: string | null;
  version: number;
}

/** Has the task reached its end on this document? (send: an ETA state; delete: discarded.) */
function atEnd(task: BulkTask, doc: { status: string; etaStatus: string | null }): boolean {
  if (task === 'send') return doc.etaStatus !== null && doc.etaStatus !== 'not_submitted';
  if (task === 'discard') return doc.status === 'discarded';
  return false;
}

/**
 * One document of a call whose answer was lost, read again with `ids` (BD-7, DEC-030 as narrowed by
 * DEC-046 for RIB-02):
 * - at the task's end state and moved there during the call → reported BY that state, as the run
 *   itself would have reported it: send `valid` «اكتمل»; `submitting`/`submitted` «لدى المصلحة
 *   الآن», followed; refused by ETA with its answer; delete `discarded` «اكتمل». No code, no retry;
 * - already at the end state before the call → the skip the server would have answered;
 * - unchanged since the review, which said the task does not apply to it → that review's skip
 *   (the same rules on the same version);
 * - moved, but not to the end state → «لم تُؤكَّد» (`unconfirmed`), retryable: a retry's dry run
 *   turns a done action into a skip;
 * - unchanged → «لم يكتمل — لم تصل النتيجة»; retryable.
 * `before` is the document as the review (dry run) read it, else as the batch held it.
 */
export function rereadResult(
  task: BulkTask,
  id: string,
  before: BeforeCall | undefined,
  now: SalesDocumentSummary | undefined,
  reviewed?: BulkResult,
): BulkResult {
  if (!now) return { id, ok: false, code: 'not_found' };
  const doc = { status: now.status, etaStatus: now.etaStatus ?? null };
  if (atEnd(task, doc)) {
    if (before && atEnd(task, { status: before.status, etaStatus: before.etaStatus }))
      return {
        id,
        ok: false,
        code: 'document_state_conflict',
        details: task === 'send' ? { etaStatus: doc.etaStatus } : {},
        ...snapshot(now),
      };
    return { id, ok: true, ...snapshot(now) };
  }
  const moved =
    !before ||
    before.status !== now.status ||
    (before.etaStatus ?? null) !== (now.etaStatus ?? null) ||
    before.version !== now.version;
  if (!moved && reviewed && !reviewed.ok && SKIP_CODES.has(reviewed.code ?? ''))
    return { ...reviewed, ...snapshot(now) };
  return {
    id,
    ok: false,
    code: 'network_error',
    details: moved ? { unconfirmed: true } : {},
    ...snapshot(now),
  };
}

/** How many rows end in each outcome. */
export function outcomeCounts(
  task: BulkTask,
  results: readonly BulkResult[],
): Record<Outcome, number> {
  const out: Record<Outcome, number> = {
    done: 0,
    waiting: 0,
    skipped: 0,
    failed: 0,
    unconfirmed: 0,
  };
  for (const r of results) out[rowState(task, r).outcome] += 1;
  return out;
}

/** Rows not settled yet: with the Tax Authority now, not completed, or not confirmed (RIB-01). */
export const unresolvedCount = (task: BulkTask, results: readonly BulkResult[]) => {
  const c = outcomeCounts(task, results);
  return c.waiting + c.failed + c.unconfirmed;
};
